<h2 mat-dialog-title i18n>Use with Class</h2>
@if (!isCreated) {
  <form [formGroup]="form" (ngSubmit)="create()">
    <mat-dialog-content class="dialog-content-scroll">
      <p class="mat-subtitle-1 accent-1">
        {{ project.metadata.title }}
        <span class="mat-caption" i18n>(Unit ID: {{ project.id }})</span>
      </p>
      <h3 i18n>1. Choose Run Type</h3>
      <div class="flex items-center gap-2">
        <mat-radio-group #runType formControlName="runType" class="flex gap-4">
          <mat-radio-button value="default" radioGroup="runType" i18n>Default</mat-radio-button>
          <mat-radio-button (click)="setAsSurveyUnit()" value="survey" radioGroup="runType" i18n
            >Survey
          </mat-radio-button>
        </mat-radio-group>
      </div>
      <p class="mat-caption pt-2">
        Default units require participants to log in with a WISE student account. Survey units are
        completed anonymously without a WISE account.
      </p>
      <p class="mat-caption" i18n>
        Note: Surveys are meant for shorter units that can be completed in one session and are best
        for anonymous data collection. Participants can only submit their responses once and cannot
        continue working at a later time. Please choose the "Default" run type if you want
        participants to be able to save their work, reload, and continue working over multiple
        sessions.
      </p>
      <mat-divider></mat-divider>
      @if (isDefaultRun()) {
        <h3 i18n>2. Choose Periods</h3>
        <p formArrayName="selectedPeriods">
          @for (control of selectedPeriodsControl.controls; track $index) {
            <span [formGroup]="control">
              <mat-checkbox formControlName="checkbox" class="period-select">
                {{ control.controls.name.value }}
              </mat-checkbox>
            </span>
          }
        </p>
        <mat-form-field appearance="fill" class="period-input form-field-long-hint">
          <mat-label i18n>Add your own periods</mat-label>
          <input matInput id="customPeriods" name="customPeriods" formControlName="customPeriods" />
        </mat-form-field>
        <p class="mat-caption" i18n>
          For "Period 9", just enter the number 9. Separate periods with commas (e.g. "Section 1,
          Section 2"). Manually named periods should be no more than 16 characters long.
        </p>
        <mat-divider></mat-divider>
        <h3 i18n>3. Choose Students Per Team</h3>
        <mat-radio-group #rGroup formControlName="maxStudentsPerTeam" class="flex gap-4">
          <mat-radio-button [value]="1" radioGroup="rGroup" i18n>Only 1 student</mat-radio-button>
          <mat-radio-button [value]="3" radioGroup="rGroup" i18n>1-3 students</mat-radio-button>
        </mat-radio-group>
        <mat-divider></mat-divider>
        <h3 i18n>4. Set Schedule</h3>
      } @else {
        <h3 i18n>2. Set Schedule</h3>
      }
      <div class="flex flex-col sm:flex-row flex-wrap sm:gap-4">
        <div>
          <mat-form-field appearance="fill" class="flex-none w-full sm:w-auto">
            <mat-label i18n>Start date</mat-label>
            <input
              matInput
              [matDatepicker]="startDatePicker"
              formControlName="startDate"
              (dateChange)="setDateRange()"
              [max]="maxStartDate"
            />
            <mat-datepicker-toggle matSuffix [for]="startDatePicker"></mat-datepicker-toggle>
            <mat-datepicker #startDatePicker></mat-datepicker>
            <mat-error i18n>Start date is required</mat-error>
          </mat-form-field>
        </div>
        <div>
          <mat-form-field appearance="fill" class="flex-none w-full sm:w-auto">
            <mat-label i18n>End date</mat-label>
            <input
              matInput
              [matDatepicker]="endDatePicker"
              formControlName="endDate"
              (dateChange)="setDateRange()"
              [min]="minEndDate"
            />
            <mat-datepicker-toggle matSuffix [for]="endDatePicker"></mat-datepicker-toggle>
            <mat-datepicker #endDatePicker></mat-datepicker>
          </mat-form-field>
        </div>
        @if (isDefaultRun()) {
          <div class="flex items-center gap-2" style="padding-bottom: 1.34375em">
            <mat-checkbox formControlName="isLockedAfterEndDate" i18n>
              Lock After End Date</mat-checkbox
            >&nbsp;
            <mat-icon
              class="info"
              tabindex="0"
              matTooltip="If the End Date has passed and the unit is locked, students will no longer be able to save new work"
              matTooltipPosition="above"
              i18n-matTooltip
              >help</mat-icon
            >
          </div>
        }
      </div>
      <p class="mat-caption !mb-4" i18n>
        Note: These dates can be changed at any time from your Class Schedule. Just select "Edit
        Settings" from the unit's dropdown menu.
      </p>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close [disabled]="isCreating" i18n>Cancel</button>
      <button
        type="submit"
        mat-flat-button
        color="primary"
        [disabled]="form.invalid || isCreating"
        class="button--progress"
      >
        @if (isCreating) {
          <mat-progress-bar mode="indeterminate"></mat-progress-bar>
        }
        <ng-container i18n>Create Run</ng-container>
      </button>
    </mat-dialog-actions>
  </form>
} @else {
  <mat-dialog-content>
    <div class="info-block">
      <p class="mat-subtitle-2" i18n>Success! This unit has been added to your Class Schedule.</p>
      <p class="mat-subtitle-1 accent-1">{{ run.name }}</p>
      @if (isDefaultRun()) {
        <p class="mat-subtitle-1 accent" i18n>Access Code: {{ run.runCode }}</p>
        <p i18n>
          Important: Every classroom unit has a unique Access Code. Students use this code to
          register for a unit. Give the code to your students when they first sign up for a WISE
          account. If they already have WISE accounts, have them log in and then select "Add Unit"
          from the student home page.
        </p>
        <p i18n>
          You can always find the Access Code for each classroom unit in your Class Schedule.
        </p>
      } @else {
        <p class="mat-subtitle-1 accent flex items-center gap-2">
          <span i18n>Access Link:</span>
          <button
            class="access-link"
            mat-button
            color="primary"
            i18n-matTooltip
            matTooltip="Copy link"
            matTooltipPosition="after"
            [cdkCopyToClipboard]="accessLinks[0]"
            (click)="copyMsg()"
          >
            <mat-icon>content_copy</mat-icon>
            <span>{{ accessLinks[0] }}</span>
          </button>
        </p>
        <p i18n>
          Important: Every survey unit has a unique Access Link. Participants can use this link to
          complete the unit without a WISE account.
        </p>
        <p i18n>You can always find the Access Link for each survey unit in your Class Schedule.</p>
      }
    </div>
  </mat-dialog-content>
  <mat-dialog-actions align="end">
    @if (isGoogleUser() && isGoogleClassroomEnabled()) {
      <button mat-button color="primary" (click)="checkClassroomAuthorization()" i18n>
        Share to Google Classroom
      </button>
    }
    <button mat-button (click)="closeAll()" i18n>Done</button>
  </mat-dialog-actions>
}
